import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, IconButton, Tooltip, useTheme, alpha } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { RecommendationsSkeleton } from './NewsSkeleton';

const NewsRecommendations = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { recommendations, loading, errors } = useSelector(state => state.legalNews);

  if (loading.recommendations) return <RecommendationsSkeleton />;

  if (errors.recommendations) {
    return (
      <Typography variant="body2" sx={{ color: theme.palette.error.main, textAlign: 'center', py: 2 }}>
        {t('news_hub.recommendations.error')}
      </Typography>
    );
  }

  if (!recommendations || recommendations.length === 0) {
    return (
      <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}>
          {t('news_hub.recommendations.none')}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<SettingsOutlinedIcon />}
          onClick={() => navigate('/legal-news/preferences')}
          sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 500 }}
        >
          {t('news_hub.recommendations.set_preferences')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
          <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
            {t('news_hub.recommendations.title')}
          </Typography>
        </Box>
        <Tooltip title={t('news_hub.recommendations.set_preferences')}>
          <IconButton
            size="small"
            onClick={() => navigate('/legal-news/preferences')}
            sx={{
              color: theme.palette.text.disabled,
              '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <SettingsOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {recommendations.map((item, index) => (
          <Box
            key={item.id}
            onClick={() => navigate(`/legal-news/${item.id}`)}
            sx={{
              py: 1.5,
              px: 1,
              mx: -1,
              borderRadius: '8px',
              cursor: 'pointer',
              borderBottom: index < recommendations.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
              transition: 'background-color 0.15s ease',
              '&:hover': { backgroundColor: theme.palette.action.hover },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 500,
                lineHeight: 1.4,
                mb: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {item.title}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.disabled,
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {item.summary}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default NewsRecommendations;
